// src/components/Sidebar/Sidebar.js
import { useContext } from 'react';
import { FaHome, FaSitemap } from "react-icons/fa";
import { FaCheckToSlot, FaTableColumns, FaUserLarge, FaUsersGear } from "react-icons/fa6";
import { IoSettingsOutline } from "react-icons/io5";
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IsAdminUser, LabelConstants } from '../../Constants.ts';
import { AuthContext } from '../../context/AuthContext.js';
import { useTheme } from '../../context/ThemeContext.js';
import icon from '../../styles/icons/logo.png';
import { UserAvatar } from '../../UIComponents/UserAvatar.ts';
import UserTypeBadge from '../../UIComponents/UserTypeBadge.tsx';
import { MdPreview } from 'react-icons/md';

const SidebarContainer = styled.div`
  width: 260px;
  background-color: ${({ theme }) => theme.colors.card};
  height: 100vh;
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${({ theme }) => theme.colors.borderLight || '#e9ecef'};
`;

const SidebarHeader = styled.div`
  padding: 6px 16px 7px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const SidebarTitle = styled.h2`
  font-size: 1.1rem; // Reduced
  margin: 0;
  display: flex;
  justify-content: start;
  align-items: center;
  height: 43px;    
`;

const SidebarMenu = styled.nav`
  flex: 1;
  padding: 8px 0; // Reduced from 20px 0
`;

const MenuItem = styled(NavLink)`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  padding: 8px 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.primary};
  }
  
  &.active {
    background-color: ${({ theme }) => theme.colors.selectedOption};
    color: ${({ theme }) => theme.colors.primary}!important;
    border-right: 4px solid ${({ theme }) => theme.colors.primary};
  }
`;

const MenuIcon = styled.span`
  margin-right: 10px;
  font-size: 1.2rem;
`;

const SidebarFooter = styled.div`
  padding: 20px 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  cursor:pointer;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.selectedOption}!important;
  }
`;

const Sidebar = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { themeConfig } = useTheme();
  const location = useLocation();
  return (
    <SidebarContainer>
      <div style={{ backgroundColor: themeConfig.colors.primary }}>
        <SidebarHeader>
          <SidebarTitle>
            <img src={icon} style={{ width: "200px" }} />
          </SidebarTitle>
        </SidebarHeader>
      </div>

      <SidebarMenu>
        <MenuItem to="/" exact="true" className="j_h_dash">
          <div className='d-flex flex-row align-items-center gap-2'>
            <FaHome size={24} />
            Dashboard
          </div>
        </MenuItem>
        <MenuItem to="/requests" className="j_h_req">
          <div className='d-flex flex-row align-items-center gap-2'>
            <FaCheckToSlot size={24} />
            {IsAdminUser(user) ? "Requests" : "My Requests"}
          </div>
        </MenuItem>
        {
          IsAdminUser(user) &&
          <MenuItem to="/users" className="j_h_users">
            <div className='d-flex flex-row align-items-center gap-2'>
              <FaUserLarge size={24} />
              Users
            </div>
          </MenuItem>
        }
        {
          IsAdminUser(user) &&
          <MenuItem to="/roles" className="j_h_roles">
            <div className='d-flex flex-row align-items-center gap-2'>
              <FaUsersGear size={24} />
              Roles
            </div>
          </MenuItem>
        }
        {
          IsAdminUser(user) &&
          <MenuItem to="/rolegroups" className="j_h_rGrp">
            <div className='d-flex flex-row align-items-center gap-2'>
              <FaSitemap size={24} />
              {LabelConstants.RoleGroups}
            </div>
          </MenuItem>
        }
        {
          IsAdminUser(user) &&
          <MenuItem to="/datapreview" className="j_h_roles">
            <div className='d-flex flex-row align-items-center gap-2'>
              <MdPreview size={24} />
              Data Preview
            </div>
          </MenuItem>
        }
        {
          IsAdminUser(user) &&
          <MenuItem to="/columnRestrictions" className="j_h_roles">
            <div className='d-flex flex-row align-items-center gap-2'>
              <FaTableColumns size={24} />
              Column Security
            </div>
          </MenuItem>
        }
        {/* {
          IsAdminUser(user) &&
          <MenuItem to="/analytics" className="j_h_analytics">
            <div className='d-flex flex-row align-items-center gap-2'>
              <SiGoogleanalytics size={24} />
              Reports Distribution
            </div>
          </MenuItem>
        } */}
        {
          IsAdminUser(user) &&
          <MenuItem to="/settings" className="j_h_settings">
            <div className='d-flex flex-row align-items-center gap-2'>
              <IoSettingsOutline size={24} />
              Settings
            </div>
          </MenuItem>
        }
      </SidebarMenu>


      <SidebarFooter
        onClick={() => {
          navigate("/profile")
        }}
        style={{
          borderRadius: '8px',
          margin: '2px 8px',
          backgroundColor: (location.pathname.toLowerCase() == "/profile") ? themeConfig.colors.selectedOption : themeConfig.colors.background,
          padding: '12px',
          borderLeft: `4px solid ${user?.userType === 2
            ? '#c0392b'
            : user?.userType === 1
              ? '#d29b00'
              : '#2164a8'
            }`,
        }}
      >
        <div className="d-flex flex-column align-items-start gap-2 j_h_uInfo">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <UserAvatar>{user.userFirstName.replace(/ /gi, "").charAt(0)}</UserAvatar>
            <div className="text-truncate d-flex flex-column">
              <div style={{ fontWeight: 'bold' }}>{user.userFirstName}</div>
              <div
                style={{
                  fontSize: '0.6rem',
                  color: '#636E72',
                  maxWidth: 150,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user.email.replace('logicpinnacle.com', 'bostondatapro.com')}
              </div>
            </div>
          </div>
          <UserTypeBadge userType={user.applicationAccess} noTitle={true} />
        </div>
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default Sidebar;